import React, { useEffect, useState } from "react";
import {
  pushSupported,
  currentPushState,
  subscribeToPush,
  unsubscribeFromPush,
} from "../lib/push";
import {
  sendPushTest,
  setDiscordNotificationWebhook,
  testDiscordNotificationWebhook,
} from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * NotificationSettings — two-channel notifications hub mounted inside the
 * Profile panel.
 *
 *   1. Web Push   — service-worker-driven OS notifications (free for all).
 *   2. Discord    — per-user opt-in webhook URL (paste it from Discord →
 *                   Channel/DM → Integrations → Webhooks).
 *
 * Both channels fan out from the same backend `services.push.notify(...)`
 * helper so users can pick either, both, or neither.
 */
export function NotificationSettings({ onToast }) {
  const { user, refresh } = useAuth();
  const [state, setState] = useState({ supported: false, permission: "default", subscribed: false });
  const [pushBusy, setPushBusy] = useState(false);
  const [discordUrl, setDiscordUrl] = useState(user?.discord_notification_webhook || "");
  const [discordBusy, setDiscordBusy] = useState(false);

  useEffect(() => { currentPushState().then(setState).catch(() => {}); }, []);
  useEffect(() => { setDiscordUrl(user?.discord_notification_webhook || ""); }, [user?.discord_notification_webhook]);

  const enable = async () => {
    setPushBusy(true);
    try {
      await subscribeToPush();
      const s = await currentPushState();
      setState(s);
      onToast?.("🔔 Push notifications enabled.");
      // Fire a self-test so the user immediately sees a notification.
      try { await sendPushTest(); } catch (_e) { /* test optional */ }
    } catch (e) {
      onToast?.(e?.message || "Couldn't enable push.");
    } finally { setPushBusy(false); }
  };

  const disable = async () => {
    setPushBusy(true);
    try {
      await unsubscribeFromPush();
      const s = await currentPushState();
      setState(s);
      onToast?.("Push notifications disabled on this device.");
    } catch (e) {
      onToast?.(e?.message || "Couldn't disable push.");
    } finally { setPushBusy(false); }
  };

  const saveDiscord = async () => {
    setDiscordBusy(true);
    try {
      await setDiscordNotificationWebhook(discordUrl.trim());
      onToast?.(discordUrl.trim() ? "Discord webhook saved." : "Discord webhook cleared.");
      await refresh?.();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't save webhook.");
    } finally { setDiscordBusy(false); }
  };

  const testDiscord = async () => {
    setDiscordBusy(true);
    try {
      await testDiscordNotificationWebhook();
      onToast?.("Sent a test to your Discord webhook — check the channel.");
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Test failed.");
    } finally { setDiscordBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="notification-settings">
      {/* ── Web Push ─────────────────────────────────────────── */}
      <section className="rounded-2xl p-4 bg-black/40 border border-[#14b8a6]/30">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">
              <i className="fas fa-bell mr-1" />WEB PUSH NOTIFICATIONS
            </p>
            <h3 className="font-display text-lg text-white mt-0.5">Browser & phone alerts</h3>
            <p className="text-[12px] text-white/55 mt-1">
              Get OS-level notifications for mentions, DMs, match invites, and
              scheduled matches — even with the tab closed.
            </p>
          </div>
          <span
            data-testid="push-status"
            className={`text-[10px] tracking-widest px-2 py-1 rounded-md font-bold ${
              state.subscribed
                ? "bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/40"
                : "bg-white/5 text-white/55 border border-white/15"
            }`}
          >
            {state.subscribed ? "● ENABLED" : "○ OFF"}
          </span>
        </div>

        {!state.supported ? (
          <p className="mt-3 text-[12px] text-[#14b8a6]/85" data-testid="push-unsupported">
            This browser doesn't support web push notifications. Try a recent
            Chrome, Edge, Firefox or Safari (iOS 16.4+).
          </p>
        ) : state.permission === "denied" ? (
          <p className="mt-3 text-[12px] text-[#14b8a6]" data-testid="push-blocked">
            Notifications are blocked at the browser level. Open this site's
            permissions and switch Notifications to "Allow", then refresh.
          </p>
        ) : (
          <div className="mt-3 flex flex-wrap gap-2">
            {state.subscribed ? (
              <>
                <button
                  type="button" onClick={disable} disabled={pushBusy}
                  data-testid="push-disable-btn"
                  className="text-[11px] tracking-widest px-4 py-2 rounded-xl btn-ghost disabled:opacity-50"
                >
                  {pushBusy ? "…" : "DISABLE ON THIS DEVICE"}
                </button>
                <button
                  type="button" onClick={() => sendPushTest().then(() => onToast?.("Test push sent."))}
                  data-testid="push-test-btn"
                  className="text-[11px] tracking-widest px-4 py-2 rounded-xl btn-neon"
                >
                  SEND TEST <i className="fas fa-paper-plane ml-1" />
                </button>
              </>
            ) : (
              <button
                type="button" onClick={enable} disabled={pushBusy}
                data-testid="push-enable-btn"
                className="text-[11px] tracking-widest px-5 py-2 rounded-xl btn-neon disabled:opacity-50"
              >
                {pushBusy ? "…" : "🔔 ENABLE PUSH NOTIFICATIONS"}
              </button>
            )}
          </div>
        )}
      </section>

      {/* ── Discord Bridge ───────────────────────────────────── */}
      <section className="rounded-2xl p-4 bg-black/40 border border-[#14b8a6]/30">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">
              <i className="fab fa-discord mr-1" />DISCORD NOTIFICATION BRIDGE
            </p>
            <h3 className="font-display text-lg text-white mt-0.5">Personal Discord webhook</h3>
            <p className="text-[12px] text-white/55 mt-1">
              Paste a webhook URL from any Discord channel or DM-bot. We'll
              POST mentions, DMs, invites, and match events to it. Optional.
            </p>
            <p className="text-[10px] text-white/40 mt-1">
              <i className="fas fa-circle-info mr-1" />
              How: Discord → Channel → ⚙ Integrations → Webhooks → New Webhook → Copy URL.
            </p>
          </div>
        </div>

        <div className="mt-3 flex gap-2 flex-wrap">
          <input
            value={discordUrl}
            onChange={(e) => setDiscordUrl(e.target.value)}
            placeholder="https://discord.com/api/webhooks/…"
            data-testid="discord-webhook-input"
            className="flex-1 min-w-[260px] bg-black/40 border border-[#14b8a6]/30 rounded-xl px-3 py-2 text-white text-sm placeholder-white/30"
          />
          <button
            type="button" onClick={saveDiscord} disabled={discordBusy}
            data-testid="discord-webhook-save"
            className="text-[11px] tracking-widest px-4 py-2 rounded-xl btn-neon disabled:opacity-50"
          >
            {discordBusy ? "…" : "SAVE"}
          </button>
          {(user?.discord_notification_webhook || "").trim() && (
            <button
              type="button" onClick={testDiscord} disabled={discordBusy}
              data-testid="discord-webhook-test"
              className="text-[11px] tracking-widest px-4 py-2 rounded-xl btn-ghost disabled:opacity-50"
            >
              TEST
            </button>
          )}
        </div>
      </section>
    </div>
  );
}

export default NotificationSettings;
